import { useCallback, useEffect, useRef, useState } from "react";
import type { MouseEvent, ReactNode } from "react";
import type { MediaItem } from "@gooner/shared";
import { isSafeMode } from "../../lib/safe-mode";
import { cx } from "../ui/primitives";
import { MediaSurface } from "../ui/MediaSurface";

/**
 * A poster thumbnail for a catalog clip. Shows the poster image (falling back to
 * a muted first frame), and plays the clip on hover — with a MOUSE; see the pointer
 * handlers below for why touch keeps the poster. Slots let callers inject any
 * buttons: `badge` top-left, `topRight` top-right, and `actions` in a hover bar along the
 * bottom edge. `onClick` makes the whole tile clickable; the slot buttons stop their own.
 *
 * The bare tile. Browsing surfaces use ClipCard (the popup, the crown, add-to-collection);
 * this is for the ones where a click means something else — a picker's select, admin.
 *
 * NEVER THE ORIGINAL AS A THUMBNAIL (QA 2026-10-29: /browse tiles pulled 7 MB GIF
 * masters to paint a 235px tile). A tile shows the clip's poster or, when it has none, the
 * {@link PosterPlaceholder} — the same box, so nothing moves. A video with no poster still
 * plays its preview on a mouse hover, and on every viewport shows a first frame from the
 * small preview rendition (never the master) over that placeholder once the tile is near
 * the screen — see {@link PreviewFrame}; a browser that will not give one up simply keeps
 * showing the placeholder instead of a black or blank tile.
 *
 * Posters are `loading="lazy"` unless the caller passes `eager` — the tiles above the fold,
 * which are what the page's first paint is waiting on.
 */
export function ClipTile({
  clip,
  badge,
  topRight,
  actions,
  onClick,
  eager = false,
}: {
  clip: MediaItem;
  badge?: ReactNode;
  topRight?: ReactNode;
  actions?: ReactNode;
  /** When set, the whole tile is clickable (e.g. to open a popup, or select). The event
   *  rides along for a caller that reads modifiers (bulk select's Shift-click range). */
  onClick?: (e: MouseEvent) => void;
  /** Load the poster straight away — for tiles above the fold. Default lazy. */
  eager?: boolean;
}) {
  const [hover, setHover] = useState(false);
  const poster = clip.posterUrl ? (
    <img
      src={clip.posterUrl}
      alt=""
      className="h-full w-full object-cover"
      draggable={false}
      loading={eager ? "eager" : "lazy"}
      decoding="async"
      // The tile is aspect-[3/4] and the img fills it; this is the box, not the file.
      width={240}
      height={320}
    />
  ) : null;

  return (
    <div
      className={cx(
        "group relative aspect-[3/4] overflow-hidden rounded-lg border border-[var(--color-edge)] bg-black",
        onClick && "cursor-pointer",
      )}
      // Pointer events, and only a mouse starts a preview. A phone synthesises
      // `mouseenter` on tap and then never fires the matching `mouseleave`, so a tapped
      // tile played forever and tapping around a 30-tile grid piled up simultaneous
      // <video>s — there is no visibility gating here to stop them. Note the asymmetry:
      // only ENTER is filtered, because `pointerleave` fires for every pointer kind, so
      // a preview that does start is always ended.
      onPointerEnter={(e) => {
        if (e.pointerType === "mouse") setHover(true);
      }}
      onPointerLeave={() => setHover(false)}
      onClick={onClick}
    >
      {clip.kind === "image" ? (
        // A still is its picture. Before this fork an image with no poster — every
        // uploaded JPEG — fell through to the <video> below and rendered black, and a
        // hover pointed a video surface at it. Uploads get posters now; one that predates
        // them gets the placeholder, NOT the master (which can be a multi-MB GIF).
        poster ?? <PosterPlaceholder />
      ) : hover ? (
        <MediaSurface
          kind="video"
          src={clip.previewUrl ?? clip.url}
          poster={clip.posterUrl}
          loop
        />
      ) : poster ? (
        poster
      ) : (
        <>
          <PosterPlaceholder />
          {/* A first frame from the PREVIEW only — never the master: a tile is no place to
              start a range read of the full clip. */}
          {clip.previewUrl ? <PreviewFrame key={clip.previewUrl} src={clip.previewUrl} /> : null}
        </>
      )}

      {badge ? <div className="absolute left-1.5 top-1.5">{badge}</div> : null}
      {topRight ? (
        <div className="absolute right-1.5 top-1.5" onClick={(e) => e.stopPropagation()}>
          {topRight}
        </div>
      ) : null}

      {actions ? (
        <div
          onClick={(e) => e.stopPropagation()}
          /*
            Revealed on hover, and always present without one.

            Tailwind v4 wraps `hover:` in `@media (hover: hover)` by DEFAULT — so on a
            touch screen `opacity-0` applied and `group-hover:opacity-100` never fired, and
            the bar's buttons (then "View ↗", the only visible way into a clip) were
            unreachable. Same fix and same reason as CardShell's footer actions: a finger
            gets the buttons, a mouse gets the clean tile the reveal was designed for.
          */
          className={cx(
            "absolute inset-x-0 bottom-0 flex items-center gap-1 bg-gradient-to-t from-black/80 to-transparent p-1.5",
            "opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100 pointer-coarse:opacity-100",
          )}
        >
          {actions}
        </div>
      ) : null}
    </div>
  );
}

/**
 * What a tile shows when its clip has no poster: the tile's own box, filled with the
 * panel colour and the same "◆" the collection cards use for a missing cover — so a
 * poster-less clip reads as "no picture yet", not as a broken or black tile, and takes
 * exactly the space a poster would (the parent is the aspect box; this only fills it).
 */
export function PosterPlaceholder({ className }: { className?: string }) {
  return (
    <div
      data-poster-placeholder=""
      aria-hidden="true"
      className={cx(
        "absolute inset-0 flex items-center justify-center bg-[var(--color-panel)] text-lg text-zinc-600",
        className,
      )}
    >
      ◆
    </div>
  );
}

/**
 * How many tiles may be fetching a first frame at once, how long one gets, and how close
 * to the viewport a tile must be before it starts.
 *
 * QA 2026-10-29 (phone /browse, 2 loads in 16): posters stayed black after scrolling and
 * were never even requested. A `<video preload="metadata">` holds its HTTP/1.1 connection
 * while a cold preview is encoded on demand (seconds to minutes), and parks on it after
 * the metadata arrives. Six poster-less tiles in one grid held all six connections Chrome
 * allows a host, and every lazy poster queued behind them for good (the eager ones had
 * gone out first). So a tile's first frame is fetched a couple at a time, copied to a
 * canvas, and its download is then CANCELLED, handing the connection back; one that never
 * paints is cancelled anyway.
 *
 * Only tiles within about a viewport of the screen start at all (QA 2026-10-29, second
 * pass: every poster-less tile on the page queued a preview, the ones far below the fold
 * included) — a tile further down fetches nothing until it scrolls close.
 */
export const PREVIEW_FRAME_SLOTS = 2;
const PREVIEW_FRAME_TIMEOUT_MS = 8000;
/** One viewport above and below the screen. */
export const PREVIEW_FRAME_ROOT_MARGIN = "100% 0px";
let previewFramesActive = 0;
const previewFrameQueue: Array<() => void> = [];

/** Run `start` once one of the {@link PREVIEW_FRAME_SLOTS} is free. Returns the release:
 *  call it exactly when done (or unmounted) — it frees the slot, or leaves the queue. */
export function takePreviewFrameSlot(start: () => void): () => void {
  let state: "queued" | "running" | "done" = "queued";
  const run = () => {
    state = "running";
    previewFramesActive++;
    start();
  };
  if (previewFramesActive < PREVIEW_FRAME_SLOTS) run();
  else previewFrameQueue.push(run);
  return () => {
    if (state === "queued") {
      const i = previewFrameQueue.indexOf(run);
      if (i >= 0) previewFrameQueue.splice(i, 1);
    } else if (state === "running") {
      previewFramesActive--;
      previewFrameQueue.shift()?.();
    }
    state = "done";
  };
}

/** Cancel a media element's download (pausing or unmounting it does not). */
function stopVideo(v: HTMLVideoElement | null) {
  if (!v) return;
  v.removeAttribute("src");
  v.load();
}

/**
 * Whether a canvas holds a picture: true when any pixel is not fully transparent.
 *
 * Drawing a `<video>` that has no decoded frame yet does not throw — it draws nothing, and
 * the canvas stays fully transparent (QA 2026-10-29, Chrome 156: drawing on `loadeddata`
 * did exactly that on every tile). A black frame is opaque, so it counts. The canvas is
 * scaled into a 16×16 probe rather than read whole. A canvas the browser will not let us
 * read (a cross-origin preview without CORS taints it) cannot be checked; the draw came
 * from a frame-ready signal, so it is trusted. Previews are same-origin
 * (`/api/media/:key/preview`), so that branch is not expected to run.
 */
export function canvasHasPicture(c: HTMLCanvasElement): boolean {
  const probe = document.createElement("canvas");
  probe.width = probe.height = 16;
  const ctx = probe.getContext("2d", { willReadFrequently: true });
  if (!ctx) return false;
  ctx.drawImage(c, 0, 0, 16, 16);
  let data: Uint8ClampedArray;
  try {
    data = ctx.getImageData(0, 0, 16, 16).data;
  } catch {
    return true; // tainted: unreadable, not blank
  }
  for (let i = 3; i < data.length; i += 4) if (data[i] > 0) return true;
  return false;
}

/**
 * A poster-less video tile's first frame, from its preview rendition, on every viewport.
 *
 *   far → (within {@link PREVIEW_FRAME_ROOT_MARGIN} of the screen) → queued → (a slot) →
 *   loading → painted | none
 *
 * While loading, the preview's metadata comes in and the frame is drawn only once one is
 * READY. Where the browser has `requestVideoFrameCallback`, the muted video plays and the
 * first presented frame is drawn (then paused); if playback is refused, or that frame is
 * blank, the video is seeked to 0.001 s and drawn on `seeked`. Without
 * `requestVideoFrameCallback` it goes straight to the seek. The canvas is then checked for
 * a picture ({@link canvasHasPicture}); only a real one is shown, and only then is the
 * download cancelled. A blank draw waits for the next signal. An error, or
 * no picture within {@link PREVIEW_FRAME_TIMEOUT_MS}, cancels the download and leaves the
 * placeholder underneath showing. Unmounting cancels too.
 *
 * Mount it keyed by `src`: a new preview is a new tile.
 */
function PreviewFrame({ src }: { src: string }) {
  const box = useRef<HTMLDivElement>(null);
  const video = useRef<HTMLVideoElement | null>(null);
  // Cancel as the element detaches, not in the effect cleanup below: on unmount React clears
  // refs before it runs passive cleanups, so `video.current` is already null there and the
  // parked download would keep its connection after its slot was handed on.
  const videoRef = useCallback((el: HTMLVideoElement | null) => {
    if (!el) stopVideo(video.current);
    video.current = el;
  }, []);
  const canvas = useRef<HTMLCanvasElement>(null);
  const release = useRef<() => void>(() => {});
  const settled = useRef(false);
  const [near, setNear] = useState(false);
  const [phase, setPhase] = useState<"far" | "queued" | "loading" | "painted" | "none">("far");

  // Far below the fold, nothing is fetched. Once near, it stays wanted.
  useEffect(() => {
    const el = box.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setNear(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setNear(true);
          io.disconnect();
        }
      },
      { rootMargin: PREVIEW_FRAME_ROOT_MARGIN },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [src]);

  useEffect(() => {
    if (!near) return;
    settled.current = false;
    setPhase("queued");
    release.current = takePreviewFrameSlot(() => setPhase("loading"));
    return () => {
      settled.current = true;
      release.current();
      release.current = () => {};
    };
  }, [src, near]);

  const finish = (painted: boolean) => {
    if (settled.current) return;
    settled.current = true;
    stopVideo(video.current);
    release.current();
    release.current = () => {};
    setPhase(painted ? "painted" : "none");
  };

  /** Draw the current frame; finish if it is a picture. A blank draw changes nothing. */
  const paint = () => {
    const v = video.current;
    const c = canvas.current;
    if (settled.current || !v || !c || !v.videoWidth || !v.videoHeight) return;
    try {
      c.width = v.videoWidth;
      c.height = v.videoHeight;
      c.getContext("2d")?.drawImage(v, 0, 0, c.width, c.height);
    } catch {
      return;
    }
    if (canvasHasPicture(c)) finish(true);
  };

  /** Ask for a frame by seeking; `seeked` then paints. No-op once settled. */
  const seekForFrame = () => {
    const v = video.current;
    if (v && !settled.current) v.currentTime = 0.001;
  };

  const onLoadedMetadata = () => {
    const v = video.current;
    if (!v || settled.current) return;
    if (typeof v.requestVideoFrameCallback !== "function") return seekForFrame();
    // Muted playback presents the first frame as soon as it is decoded (Chrome on a
    // 2.5 MB/s link: 70–146 KB into a 507 KB preview). A paused seek paints too, but only
    // once Chrome has buffered the whole preview (2026-10-29), so it is the fallback: when
    // playback is refused (autoplay off, Low Power Mode) or its first frame is blank. On a
    // fast link the whole preview can land before either, and is then cancelled anyway.
    v.requestVideoFrameCallback(() => {
      v.pause();
      paint();
      seekForFrame();
    });
    v.muted = true;
    v.play()?.catch(seekForFrame);
  };

  useEffect(() => {
    if (phase !== "loading") return;
    const t = window.setTimeout(() => finish(false), PREVIEW_FRAME_TIMEOUT_MS);
    return () => window.clearTimeout(t);
    // Keyed on the phase alone: `finish` only reads refs, and re-arming on its identity
    // would restart the clock on every render.
  }, [phase]);

  return (
    // The box is what the observer watches: the canvas is display:none until painted,
    // and a display:none element never intersects anything.
    <div ref={box} aria-hidden="true" className="pointer-events-none absolute inset-0">
      <canvas
        ref={canvas}
        data-preview-frame={phase}
        className={cx("absolute inset-0 h-full w-full object-cover", phase !== "painted" && "hidden")}
      />
      {phase === "loading" ? (
        <video
          ref={videoRef}
          src={src}
          className="absolute inset-0 h-full w-full object-cover"
          muted
          playsInline
          preload="metadata"
          disablePictureInPicture={isSafeMode()}
          onLoadedMetadata={onLoadedMetadata}
          onSeeked={paint}
          onError={() => finish(false)}
        />
      ) : null}
    </div>
  );
}
